import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, ChevronRight, ImageOff, Loader2, RefreshCw, Upload } from 'lucide-react';
import type { DirtyForm } from '../../hooks/useDirtyForm';
import { ChangedDot, SheetField, SheetRow, SheetSection } from '../settings/sheet';
import { SettingsSwitch } from '../settings/storeSettingsForm';
import TicketPaper from './TicketPaper';
import {
  DELIVERY_FLAGS, DeliveryFlag, ITEM_SIZES, ItemSize, PreviewKey, PreviewSlide, PrintingPrinter, printingApi,
} from './printingApi';
import {
  END_TEXT_LINES_MAX, END_TEXT_MAX, HEADER_LINES_MAX, HEADER_MAX, LOOK_KEYS, PageForm, TICKET_OPTION_KEYS,
  previewPaper, receiptPrinters, textLineCount, ticketPrinters,
} from './printingModel';
import { GHOST_BTN, ICON_BTN, INPUT_CLS, Notice } from './printingUi';
import { LogoError, prepareLogo } from './receiptLogo';

/**
 * «Πώς βγαίνει κάθε χαρτί» (απόφαση ιδιοκτήτη 25/10/2026): κάθε χαρτί — απόδειξη, δελτίο Take Away,
 * δελτίο τραπεζιού, δελτίο delivery — σε προεπισκόπηση που αλλάζει με swipe αριστερά/δεξιά (ή με τις
 * καρτέλες/τα βελάκια), και δίπλα οι λίγες ρυθμίσεις του: της απόδειξης λογότυπο/κεφαλίδα/κείμενο τέλους,
 * του delivery μέγεθος ειδών και ποια στοιχεία φαίνονται, των δελτίων τα «Έντονα γράμματα».
 * Το χαρτί το φτιάχνει ο server με τον ΙΔΙΟ formatter της εκτύπωσης (POST /printing/preview), με ό,τι
 * δείχνει τώρα η φόρμα — και πριν την «Αποθήκευση».
 */

interface Props {
  storeId: number;
  form: DirtyForm<PageForm>;
  printers: PrintingPrinter[];
  takeaway: boolean;
  dineIn: boolean;
}

const PREVIEW_DEBOUNCE_MS = 300;
// Χαρτί 80 mm (~324 px) + κενό + ρυθμίσεις (~340 px).
const WIDE_MIN_PX = 700;

type PreviewState = { status: 'loading' | 'ready' | 'error'; slides: PreviewSlide[] };

const reducedMotion = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Επιλογή-«κουτί» (radio/checkbox) με εγγενές input — ίδιο ύφος με τη φόρμα εκτυπωτή. */
const Choice: React.FC<{ type: 'radio' | 'checkbox'; name?: string; checked: boolean; onChange: (checked: boolean) => void; label: string }> = ({
  type, name, checked, onChange, label,
}) => (
  <label className="relative inline-flex">
    <input type={type} name={name} className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span
      className={`inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-colors cursor-pointer
        peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1
        ${checked ? 'border-brand bg-brand-soft text-brand' : 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3'}`}
    >
      {type === 'checkbox' && (
        <span className={`grid place-items-center w-4 h-4 rounded border ${checked ? 'border-brand bg-brand text-brand-fg' : 'border-line-strong'}`} aria-hidden="true">
          {checked && <Check className="w-3 h-3" />}
        </span>
      )}
      {label}
    </span>
  </label>
);

const LookSection: React.FC<Props> = ({ storeId, form, printers, takeaway, dineIn }) => {
  const { t } = useTranslation(['printing']);
  const { values: v, setField, changedKeys } = form;
  const keys = useMemo<PreviewKey[]>(
    () => (['receipt', takeaway ? 'takeaway' : null, dineIn ? 'dinein' : null, 'delivery'] as Array<PreviewKey | null>)
      .filter((k): k is PreviewKey => k !== null),
    [takeaway, dineIn],
  );
  const [active, setActive] = useState(0);
  const current = keys[Math.min(active, keys.length - 1)];
  const [preview, setPreview] = useState<PreviewState>({ status: 'loading', slides: [] });
  const [reloadTick, setReloadTick] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [trackHeight, setTrackHeight] = useState<number | null>(null);
  const changed = (key: keyof PageForm) => changedKeys.includes(key);
  // Δύο στήλες (χαρτί | ρυθμίσεις) μόνο όταν χωράνε· αλλιώς οι ρυθμίσεις ΠΑΝΩ από το χαρτί, για να μην
  // κρύβονται κάτω από μια μακριά απόδειξη. Από το πλάτος της ενότητας, όχι της οθόνης (το φύλλο έχει
  // δική του αριστερή στήλη).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(false);
  // Πόσο χωρά το χαρτί (στο κινητό λιγότερο από τα 80 mm της οθόνης): μικραίνει, δεν κόβεται.
  const [paperRoom, setPaperRoom] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    const track = trackRef.current;
    if (!el) return undefined;
    // Αμέσως (και χωρίς ResizeObserver), μετά σε κάθε αλλαγή μεγέθους.
    const measure = () => {
      if (el.clientWidth > 0) setWide(el.clientWidth >= WIDE_MIN_PX);
      if (track && track.clientWidth > 0) setPaperRoom(track.clientWidth - 8);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (track) ro.observe(track);
    return () => ro.disconnect();
  }, []);

  // ── Προεπισκόπηση από τον server: ό,τι δείχνει η φόρμα, με μικρή καθυστέρηση στο γράψιμο ──
  const body = useMemo(() => ({
    slides: keys,
    paper: previewPaper(printers, v),
    look: { has_logo: !!v.logo_url, header_text: v.header_text, thank_you_text: v.thank_you_text },
    ...(v.deliveryTicket ? { ticket_options: { delivery: v.deliveryTicket } } : {}),
  }), [keys, printers, v]);
  const bodyKey = JSON.stringify(body);
  const seq = useRef(0);
  useEffect(() => {
    const id = ++seq.current;
    setPreview(prev => ({ ...prev, status: prev.slides.length > 0 ? prev.status : 'loading' }));
    const timer = setTimeout(async () => {
      const r = await printingApi.preview(storeId, JSON.parse(bodyKey));
      if (id !== seq.current) return;
      if (r.ok && r.data) setPreview({ status: 'ready', slides: r.data });
      else setPreview(prev => ({ status: prev.slides.length > 0 ? 'ready' : 'error', slides: prev.slides }));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [storeId, bodyKey, reloadTick]);

  // ── Swipe: οριζόντια κύλιση με «κούμπωμα» ανά χαρτί· το ενεργό από τη θέση της κύλισης ──
  const go = useCallback((i: number) => {
    const next = Math.max(0, Math.min(keys.length - 1, i));
    setActive(next);
    const el = trackRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ left: next * el.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, [keys.length]);
  const onScroll = () => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== active && i >= 0 && i < keys.length) setActive(i);
  };
  // Το ύψος της ζώνης = του χαρτιού που φαίνεται (όχι του μακρύτερου) — και όταν αλλάζει (λογότυπο που
  // φορτώνει, νέα προεπισκόπηση).
  useLayoutEffect(() => {
    const el = slideRefs.current[active];
    if (trackRef.current) trackRef.current.scrollTop = 0;
    if (!el) { setTrackHeight(null); return undefined; }
    setTrackHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTrackHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
    // paperRoom/wide: το χαρτί αλλάζει πλάτος άρα και ύψος (και χωρίς ResizeObserver).
  }, [active, preview.slides, paperRoom, wide]);

  const slideOf = (key: PreviewKey) => preview.slides.find(s => s.key === key) || null;
  const titleOf = (key: PreviewKey) => t(`printing:look.slides.${key}`);
  const onTabKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
  };

  // ── Λογότυπο ──
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // ξανά το ίδιο αρχείο → ξανά change
    if (!file) return;
    setLogoError(null);
    setLogoBusy(true);
    try {
      setField('logo_url', await prepareLogo(file));
    } catch (err) {
      const code = err instanceof LogoError ? err.code : 'read';
      setLogoError(t(`printing:receipt.logoError.${code}`));
    } finally {
      setLogoBusy(false);
    }
  };
  const textOnlyReceipts = receiptPrinters(printers).filter(p => p.print_method === 'text');
  const textOnlyTickets = ticketPrinters(printers).filter(p => p.print_method === 'text');
  const tooManyHeaderLines = textLineCount(v.header_text) > HEADER_LINES_MAX;
  const tooManyEndLines = textLineCount(v.thank_you_text) > END_TEXT_LINES_MAX;

  const setDelivery = (patch: Partial<NonNullable<PageForm['deliveryTicket']>>) =>
    form.setValues(prev => (prev.deliveryTicket ? { ...prev, deliveryTicket: { ...prev.deliveryTicket, ...patch } } : prev));

  const boldRow = v.ticket_bold !== null && (
    <div data-testid="ticket-bold">
      <SheetRow
        title={t('printing:routing.ticketBold')}
        subtitle={t('printing:routing.ticketBoldSub')}
        changed={changed('ticket_bold')}
        control={({ labelId, descriptionId }) => (
          <SettingsSwitch checked={v.ticket_bold === true} onChange={next => setField('ticket_bold', next)} labelId={labelId} describedById={descriptionId} />
        )}
      />
      {textOnlyTickets.length > 0 && (
        <p className="xs-row-note" data-testid="ticket-bold-text-printers">
          {t('printing:routing.ticketBoldTextOnly', { count: textOnlyTickets.length, names: textOnlyTickets.map(p => `«${p.name}»`).join(', ') })}
        </p>
      )}
    </div>
  );

  const receiptOptions = (
    <div className="space-y-6" data-testid="options-receipt">
      <SheetField label={t('printing:receipt.logo')} changed={changed('logo_url')} hint={t('printing:receipt.logoHint')}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-28 h-20 shrink-0 rounded-lg border border-line bg-white grid place-items-center overflow-hidden" data-testid="logo-thumb">
            {v.logo_url
              ? <img src={v.logo_url} alt={t('printing:receipt.logoAlt')} className="max-w-full max-h-full object-contain" />
              : <span className="text-xs text-neutral-500">{t('printing:receipt.logoNone')}</span>}
          </div>
          <label className={`${GHOST_BTN} cursor-pointer focus-within:ring-2 focus-within:ring-brand`}>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onFile} disabled={logoBusy}
              aria-label={v.logo_url ? t('printing:receipt.logoReplace') : t('printing:receipt.logoUpload')} />
            {logoBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
            <span aria-hidden="true">{v.logo_url ? t('printing:receipt.logoReplace') : t('printing:receipt.logoUpload')}</span>
          </label>
          {v.logo_url && (
            <button type="button" onClick={() => setField('logo_url', null)} className={GHOST_BTN}>
              <ImageOff className="w-4 h-4" aria-hidden="true" />{t('printing:receipt.logoRemove')}
            </button>
          )}
        </div>
        {logoError && <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{logoError}</p>}
        {v.logo_url && textOnlyReceipts.length > 0 && (
          <div className="mt-3">
            <Notice testId="logo-text-method">{t('printing:receipt.textMethodNote', { names: textOnlyReceipts.map(p => `«${p.name}»`).join(', '), count: textOnlyReceipts.length })}</Notice>
          </div>
        )}
      </SheetField>

      <SheetField label={t('printing:receipt.header')} htmlFor="printing-header" changed={changed('header_text')}
        hint={tooManyHeaderLines
          ? <span className="text-red-600 dark:text-red-400">{t('printing:receipt.tooManyLines', { max: HEADER_LINES_MAX })}</span>
          : t('printing:receipt.headerHint')}>
        <textarea id="printing-header" value={v.header_text} maxLength={HEADER_MAX} rows={2}
          placeholder={t('printing:receipt.headerPlaceholder')} aria-describedby="printing-header-hint"
          aria-invalid={tooManyHeaderLines || undefined}
          onChange={e => setField('header_text', e.target.value)} className={`${INPUT_CLS} resize-y`} />
      </SheetField>

      <SheetField label={t('printing:receipt.endText')} htmlFor="printing-end-text" changed={changed('thank_you_text')}
        hint={tooManyEndLines
          ? <span className="text-red-600 dark:text-red-400">{t('printing:receipt.tooManyLines', { max: END_TEXT_LINES_MAX })}</span>
          : t('printing:receipt.endTextHint')}>
        <textarea id="printing-end-text" value={v.thank_you_text} maxLength={END_TEXT_MAX} rows={3}
          placeholder={t('printing:receipt.endTextPlaceholder')} aria-describedby="printing-end-text-hint"
          aria-invalid={tooManyEndLines || undefined}
          onChange={e => setField('thank_you_text', e.target.value)} className={`${INPUT_CLS} resize-y`} />
      </SheetField>
    </div>
  );

  const kitchenOptions = (
    <div className="space-y-4" data-testid="options-kitchen">
      <p className="text-sm leading-[21px] text-t2">{t('printing:look.kitchenNote')}</p>
      {boldRow}
    </div>
  );

  const d = v.deliveryTicket;
  const deliveryOptions = (
    <div className="space-y-6" data-testid="options-delivery">
      {d && (
        <>
          <fieldset>
            <legend className="xs-label mb-2">{t('printing:look.itemSize')}{changed('deliveryTicket') && <ChangedDot />}</legend>
            <div className="flex flex-wrap gap-2">
              {ITEM_SIZES.map((size: ItemSize) => (
                <Choice key={size} type="radio" name="printing-item-size" checked={d.item_size === size}
                  onChange={() => setDelivery({ item_size: size })} label={t(`printing:look.sizes.${size}`)} />
              ))}
            </div>
            <p className="xs-field-hint">{t(`printing:look.sizeHint.${d.item_size}`)}</p>
          </fieldset>
          <fieldset>
            <legend className="xs-label mb-2">{t('printing:look.showTitle')}</legend>
            <div className="flex flex-wrap gap-2">
              {DELIVERY_FLAGS.map((k: DeliveryFlag) => (
                <Choice key={k} type="checkbox" checked={d[k]} onChange={c => setDelivery({ [k]: c })} label={t(`printing:look.show.${k}`)} />
              ))}
            </div>
            <p className="xs-field-hint">{t('printing:look.alwaysShown')}</p>
          </fieldset>
        </>
      )}
      {boldRow}
    </div>
  );

  const optionsFor = (key: PreviewKey) => (key === 'receipt' ? receiptOptions : key === 'delivery' ? deliveryOptions : kitchenOptions);
  const dirty = changedKeys.some(k => LOOK_KEYS.includes(k) || TICKET_OPTION_KEYS.includes(k) || k === 'ticket_bold');

  return (
    <SheetSection id="printing-look" title={t('printing:look.title')} hint={t('printing:look.hint')} dirty={dirty} testId="look-section">
      <div role="tablist" aria-label={t('printing:look.tabsAria')} className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" onKeyDown={onTabKey}>
        {keys.map((key, i) => (
          <button key={key} type="button" role="tab" id={`printing-look-tab-${key}`} aria-selected={i === active}
            aria-controls={`printing-look-panel-${key}`} tabIndex={i === active ? 0 : -1} onClick={() => go(i)}
            className={`shrink-0 min-h-[40px] px-3.5 rounded-full border text-sm font-semibold transition-colors
              ${i === active ? 'border-brand bg-brand-soft text-brand' : 'border-line bg-surface-2 text-t2 hover:bg-surface-3'}`}
            data-testid={`look-tab-${key}`}>
            {titleOf(key)}
          </button>
        ))}
      </div>

      <div ref={bodyRef} className={`grid gap-x-8 gap-y-6 items-start ${wide ? 'grid-cols-[340px_minmax(0,1fr)]' : 'grid-cols-1'}`}
        data-layout={wide ? 'wide' : 'narrow'}>
        <div className={`min-w-0 ${wide ? '' : 'order-2'}`}>
          <div className="relative">
            <div
              ref={trackRef}
              onScroll={onScroll}
              className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden transition-[height] duration-200"
              style={{ height: trackHeight ?? undefined, alignItems: 'flex-start' }}
              aria-roledescription={t('printing:look.carousel')}
              aria-label={t('printing:look.carouselAria')}
              data-testid="look-track"
            >
              {keys.map((key, i) => {
                const slide = slideOf(key);
                return (
                  <div key={key} ref={el => { slideRefs.current[i] = el; }} id={`printing-look-panel-${key}`} role="tabpanel"
                    aria-labelledby={`printing-look-tab-${key}`} className="snap-center shrink-0 w-full px-1 py-2" data-testid={`look-slide-${key}`}>
                    {slide ? (
                      <TicketPaper commands={slide.commands} paperWidth={slide.paper_width} logo={v.logo_url}
                        heavy={key !== 'receipt' && v.ticket_bold === true} label={titleOf(key)} maxWidth={paperRoom} />
                    ) : preview.status === 'error' ? (
                      <div className="py-10 text-center space-y-3">
                        <p className="text-sm text-t2">{t('printing:look.previewFailed')}</p>
                        <button type="button" className={GHOST_BTN} onClick={() => setReloadTick(x => x + 1)}>
                          <RefreshCw className="w-4 h-4" aria-hidden="true" />{t('printing:look.retry')}
                        </button>
                      </div>
                    ) : (
                      <div className="py-16 grid place-items-center text-t3"><Loader2 className="w-5 h-5 animate-spin" aria-label={t('printing:look.loading')} /></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <button type="button" className={ICON_BTN} onClick={() => go(active - 1)} disabled={active === 0} aria-label={t('printing:look.prev')}>
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <span className="flex items-center gap-1.5" aria-hidden="true">
              {keys.map((key, i) => (
                <span key={key} className={`h-1.5 rounded-full transition-all ${i === active ? 'w-5 bg-brand' : 'w-1.5 bg-line-strong'}`} />
              ))}
            </span>
            <button type="button" className={ICON_BTN} onClick={() => go(active + 1)} disabled={active === keys.length - 1} aria-label={t('printing:look.next')}>
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
          <p className="mt-2 text-center text-xs text-t3">{t('printing:look.sample')}</p>
        </div>

        <div className={`min-w-0 ${wide ? 'sticky top-4' : 'order-1'}`} data-testid={`look-options-${current}`}>
          <h3 className="text-base font-bold text-t1 mb-4">{titleOf(current)}</h3>
          {optionsFor(current)}
        </div>
      </div>
    </SheetSection>
  );
};

export default LookSection;
